import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { removeItemFromCache, addItemToCache } from '@ts-core/core/utils/apolloUtils';
import { getErrorMessage } from '@ts-core/lib/apollo';

import { getAssignmentsQuery, GetAssignmentsQueryResult, GetAssignmentsQueryVariables } from '../graphql/queries/getAssignmentsQuery';
import {
  toggleAssignmentMutation,
  ToggleAssignmentMutationResult,
  ToggleAssignmentMutationVariables,
} from '../graphql/mutations/toggleAssignmentMutation';
import {
  removeAssignmentMutation,
  RemoveAssignmentMutationResult,
  RemoveAssignmentMutationVariables,
} from '../graphql/mutations/removeAssignmentMutation';
import { sendAssignmentMutation, SendAssignmentMutationResult, SendAssignmentMutationVariables } from '../graphql/mutations/sendAssignmentMutation';
import {
  createAssignmentMutation,
  CreateAssignmentMutationResult,
  CreateAssignmentMutationVariables,
} from '../graphql/mutations/createAssignmentMutation';
import {
  updateAssignmentMutation,
  UpdateAssignmentMutationResult,
  UpdateAssignmentMutationVariables,
} from '../graphql/mutations/updateAssignmentMutation';

import { Assignment } from '../types';
import { AssignmentFormInput } from '../types/form';

interface AssignmentActionsHookInput {
  courseId: string;
}

interface AssignmentActionsHookOutput {
  onToggleAssignment: (assignment: Assignment) => void;
  onSendAssignment: (assignment: Assignment) => void;
  onRemoveAssignment: (assignment: Assignment) => void;

  onCreateAssignment: (assignment: AssignmentFormInput, onSuccess?: (assignment: Assignment) => void) => void;
  createAssignmentLoading: boolean;
  createAssignmentError?: string;

  onUpdateAssignment: (assignmentId: string, assignment: AssignmentFormInput, onSuccess?: (assignment: Assignment) => void) => void;
  updateAssignmentLoading: boolean;
  updateAssignmentError?: string;
}

export default function useAssignmentActions(input: AssignmentActionsHookInput): AssignmentActionsHookOutput {
  const { courseId } = input;

  const { showErrorNotification, showSuccessNotification } = useSnackbarNotifications();

  /**
   * Create Assignment
   */
  const [createAssignment, { loading: createAssignmentLoading, error: createAssignmentError }] = useMutation<
    CreateAssignmentMutationResult,
    CreateAssignmentMutationVariables
  >(createAssignmentMutation, {
    onError: showErrorNotification,
    update: addItemToCache<CreateAssignmentMutationResult, GetAssignmentsQueryResult, GetAssignmentsQueryVariables>({
      query: getAssignmentsQuery,
      queryVariables: { courseId },
      queryName: 'assignments',
      mutationName: 'assignment',
    }),
  });
  const onCreateAssignment = useCallback(
    (input: AssignmentFormInput, onSuccess?: (assignment: Assignment) => void) => {
      createAssignment({
        variables: {
          courseId,
          input,
        },
      }).then(({ data }) => {
        if (onSuccess && data?.assignment) {
          onSuccess(data.assignment);
        }
      });
    },
    [createAssignment, courseId]
  );

  /**
   * Update Assignment
   */
  const [updateAssignment, { loading: updateAssignmentLoading, error: updateAssignmentError }] = useMutation<
    UpdateAssignmentMutationResult,
    UpdateAssignmentMutationVariables
  >(updateAssignmentMutation, {
    onError: showErrorNotification,
    onCompleted: () => showSuccessNotification(),
  });
  const onUpdateAssignment = useCallback(
    (assignmentId: string, input: AssignmentFormInput, onSuccess?: (assignment: Assignment) => void) => {
      updateAssignment({
        variables: {
          courseId,
          assignmentId,
          input,
        },
      }).then(({ data }) => {
        if (onSuccess && data?.assignment) {
          onSuccess(data.assignment);
        }
      });
    },
    [updateAssignment, courseId]
  );

  /**
   * Toggle Assignment
   */
  const [toggleAssignment] = useMutation<ToggleAssignmentMutationResult, ToggleAssignmentMutationVariables>(toggleAssignmentMutation, {
    onError: showErrorNotification,
  });
  const onToggleAssignment = useCallback(
    (assignment: Assignment) => {
      const updatedAssignment = { ...assignment, enabled: !assignment.enabled };
      toggleAssignment({
        variables: {
          courseId,
          assignmentId: assignment.id,
          action: updatedAssignment.enabled ? 'enable' : 'disable',
        },
        optimisticResponse: {
          assignment: updatedAssignment,
        },
      });
    },
    [toggleAssignment, courseId]
  );

  /**
   * Send Assignment
   */
  const [sendAssignment] = useMutation<SendAssignmentMutationResult, SendAssignmentMutationVariables>(sendAssignmentMutation, {
    onError: showErrorNotification,
  });
  const onSendAssignment = useCallback(
    (assignment: Assignment) => {
      sendAssignment({
        variables: {
          courseId,
          assignmentId: assignment.id,
        },
        optimisticResponse: {
          assignment: {
            ...assignment,
            sent: true,
          },
        },
      });
    },
    [sendAssignment, courseId]
  );

  /**
   * Remove Assignment
   */
  const [removeAssignment] = useMutation<RemoveAssignmentMutationResult, RemoveAssignmentMutationVariables>(removeAssignmentMutation, {
    onError: showErrorNotification,
    update: removeItemFromCache<RemoveAssignmentMutationResult, GetAssignmentsQueryResult, GetAssignmentsQueryVariables>({
      query: getAssignmentsQuery,
      queryName: 'assignments',
      queryVariables: {
        courseId,
      },
      mutationName: 'assignment',
      idAccessor: 'id',
    }),
  });
  const onRemoveAssignment = useCallback(
    (assignment: Assignment) => {
      removeAssignment({
        variables: {
          courseId,
          assignmentId: assignment.id,
        },
        optimisticResponse: {
          assignment: {
            id: assignment.id,
          },
        },
      });
    },
    [removeAssignment, courseId]
  );

  return {
    onToggleAssignment,
    onSendAssignment,
    onRemoveAssignment,

    onCreateAssignment,
    createAssignmentLoading,
    createAssignmentError: getErrorMessage({ error: createAssignmentError }),

    onUpdateAssignment,
    updateAssignmentLoading,
    updateAssignmentError: getErrorMessage({ error: updateAssignmentError }),
  };
}
